import { useEffect, useRef, useState } from 'react'
import { Form, useNavigate } from 'react-router-dom';
import { Alert, Button, ButtonGroup, Checkbox, FormControlLabel, IconButton, InputLabel, MenuItem, Select, TextField } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers';
import dayjs from 'dayjs';

import { ICustomerLight } from '../../types/customer';
import { ISelectOption } from '../../types/list';

import { IWasteCharacterization } from '../../types/wasteCharacterization';

import { CustomerService } from '../../services/customerService';
import { MaterialPriceService } from '../../services/materialPriceService';
import { IMaterialPrice } from '../../types/material';
import { UtilityService } from '../../services/utilityService';
import { WasteCharacterizationService } from '../../services/wasteCharacterizationService';
import { useUserContext } from '../../contexts/userContext';

import Json from '../json';

import '../../styles/wasteCharacterizationAddForm.scss';


const WasteCharacterizationAddForm = () => {
	const navigate = useNavigate();
	const userContext = useUserContext();
	const [formFields, setFormFields] = useState<IWasteCharacterization>({
		customerId: 0,
		materialId: 0,
		constituents: '',
		dotWasteStream: '',
		byProcess: false,
		byTesting: false,
		includeProfileDocument: false,
		testingDocContent: undefined,
		isSolid: false,
		isIgnitable: false,
		isCorrosive: false,
		isReactive: false,
		isToxic: false,
		rcraWasteDetermination: undefined,
		profileDocumentContent: undefined,
		isLdrApplicable: false,
		isWastewaterTreatable: false,
		expiresOn: new Date(),
		createdBy: '',
		createdByTitle: '',
		createdByEmail: '',
		status: 'Created'
	});
	const [customerOptions, setCustomerOptions] = useState<ISelectOption[]>([]);
	const [materialOptions, setMaterialOptions] = useState<ISelectOption[]>([]);
	const [isSaving, setIsSaving] = useState(false);
	const [errorMessages, setErrorMessages] = useState<string[] | undefined>();
	const [isLoadingCustomers, setIsLoadingCustomers] = useState(true);
	const [isLoadingMaterialPrices, setIsLoadingMaterialPrices] = useState(true);
	const [processDocFilename, setProcessDocFileName] = useState<string>('');
	const [testingDocFilename, setTestingDocFileName] = useState<string>('');
	const [profileDocumentFilename, setProfileDocumentFileName] = useState<string>('');

	const allowedTilsCustomerIds = userContext.tilsAccess.map(c => c.customerId);

	const physicalStateOptions = ["Solid", "Liquid", "Gas", "Multi-Layer"] as const;
	const rcraWasteDeterminationOptions = ["Non-Hazardous", "Hazardous", "Mixed", "Used Oil", "Universal Waste", "Exempt"] as const;
	const statusOptions = ['Created', 'Emailed', 'Completed'] as const;

	const processDocRef = useRef(null);
	const testingDocRef = useRef(null);
	const profileDocumentRef = useRef(null);

	useEffect(() => {
		loadCustomers();
	}, [])

	useEffect(() => {
		if (formFields.customerId && formFields.customerId > 0) {
			loadMaterials();
		}
	}, [formFields.customerId])

	useEffect(() => {
		if (formFields.byProcess == false) {
			setFormFields({ ...formFields, ["processDocFormFile"]: undefined });
			setProcessDocFileName('');
			if (processDocRef && processDocRef.current) {
				// @ts-ignore
				processDocRef.current.value = null;
			}
		}
	}, [formFields.byProcess])

	useEffect(() => {
		if (formFields.byTesting == false) {
			setFormFields({ ...formFields, ["testingDocFormFile"]: undefined });
			setTestingDocFileName('');
			if (testingDocRef && testingDocRef.current) {
				// @ts-ignore
				testingDocRef.current.value = null;
			}
		}
	}, [formFields.byTesting])

	useEffect(() => {
		if (formFields.includeProfileDocument == false) {
			setFormFields({ ...formFields, ["profileDocumentFormFile"]: undefined });
			setProfileDocumentFileName('');
			if (profileDocumentRef && profileDocumentRef.current) {
				// @ts-ignore
				profileDocumentRef.current.value = null;
			}
		}
	}, [formFields.includeProfileDocument])

	const loadCustomers = () => {
		setIsLoadingCustomers(true);
		setCustomerOptions([]);
		setFormFields({ ...formFields, ["customerId"]: 0 });
		CustomerService.getCustomers()
			.then((response) => {
				console.log('CustomerService.getCustomers: SUCCESS', response);
				const options: ISelectOption[] = [];
				const activeCustomers = CustomerService
					.mapCustomerLightList(response.data)
					.filter((item) => item.status == 'Active')
					.filter((item) => allowedTilsCustomerIds.includes(+item.id));

				activeCustomers.map((item: ICustomerLight) => {
					if (item.name) {
						options.push({
							"name": item.name,
							"value": item.id
						})
					}
				});
				setCustomerOptions(options.sort((a, b) => UtilityService.compareStrings(a.name, b.name)));

				if (options.length > 0) {
					setFormFields({ ...formFields, ["customerId"]: parseInt(options[0].value) });
				}
				else {
					setFormFields({ ...formFields, ["customerId"]: -1 });
				}
			})
			.catch((error) => {
				console.log('CustomerService.getCustomers: ERROR', error);
			})
			.finally(() => {
				setIsLoadingCustomers(false);
			});
	}

	const loadMaterials = () => {
		setIsLoadingMaterialPrices(true);
		setMaterialOptions([]);
		setFormFields({ ...formFields, ["materialId"]: 0 });

		MaterialPriceService.getMaterialPricesForCustomer(formFields.customerId)
			.then((response) => {
				console.log('MaterialService.getMaterialsForCustomer: SUCCESS', response);
				const options: ISelectOption[] = [];
				const materials = MaterialPriceService.mapMaterialPrices(response.data);

				materials.map((item: IMaterialPrice) => {
					// We only need a list of materials themselves, not pricing, so filter out duplicate prices from other vendors
					const existingOption = options.find(o => o.value == item.materialId.toString());
					if (!existingOption) {
						options.push({
							"name": item.materialName,
							"value": item.materialId.toString()
						})
					}
				});

				setMaterialOptions(options.sort((a, b) => UtilityService.compareStrings(a.name, b.name)));

				if (options.length > 0) {
					setFormFields({ ...formFields, ["materialId"]: parseInt(options[0].value) });
				}
				else {
					setFormFields({ ...formFields, ["materialId"]: -1 });
				}
			})
			.catch((error) => {
				console.log('MaterialService.getMaterialsForCustomer: ERROR', error);
			})
			.finally(() => {
				setIsLoadingMaterialPrices(false);
			});
	}

	const handleSubmit = async () => {
		setErrorMessages(undefined);
		if (!validateForm()) return;

		setIsSaving(true);
		WasteCharacterizationService.addWasteCharacterization(formFields)
			.then((response) => {
				console.log('WasteCharacterizationService.addWasteCharacterization: SUCCESS', response);
				var record = WasteCharacterizationService.mapWasteCharacterization(response.data?.entity);
				navigate('/wastecharacterization/' + record.id);
			})
			.catch((error) => {
				console.log('WasteCharacterizationService.addWasteCharacterization: ERROR', error);
				setErrorMessages(["We encountered an error while saving the waste characterization. Please try again."]);
			})
			.finally(() => {
				setIsSaving(false);
			});
	}

	const handleByProcessChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		setFormFields({ ...formFields, ["byProcess"]: event.target.checked })
	}

	const handleByTestingChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		setFormFields({ ...formFields, ["byTesting"]: event.target.checked })
	}

	const handleIncludeProfileDocumentChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		setFormFields({ ...formFields, ["includeProfileDocument"]: event.target.checked })
	}

	const handleProcessDocChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		if (event.target.files && event.target.files.length > 0) {
			const file = event.target.files[0];
			setFormFields({ ...formFields, ["processDocFormFile"]: file });
			setProcessDocFileName(file.name);
		}
	}

	const handleTestingDocChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		if (event.target.files && event.target.files.length > 0) {
			const file = event.target.files[0];
			setFormFields({ ...formFields, ["testingDocFormFile"]: file });
			setTestingDocFileName(file.name);
		}
	}

	const handleProfileDocumentChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		if (event.target.files && event.target.files.length > 0) {
			const file = event.target.files[0];
			setFormFields({ ...formFields, ["profileDocumentFormFile"]: file });
			setProfileDocumentFileName(file.name);
		}
	}

	const validateForm = (): boolean => {
		let errors = [];
		if (formFields?.byProcess && !processDocFilename) {
			errors.push("An SDS document must be provided if waste determination is based on Process Evaluation.");
		}
		if (formFields?.byProcess && processDocFilename.length > 4 && processDocFilename.slice(-4).toLowerCase() != '.pdf') {
			errors.push("The SDS document must be a PDF file.");
		}
		if (formFields?.byTesting && !testingDocFilename) {
			errors.push("A TCLP Testing Information document must be provided if waste analysis was performed.");
		}
		if (formFields?.byTesting && testingDocFilename.length > 4 && testingDocFilename.slice(-4).toLowerCase() != '.pdf') {
			errors.push("The TCLP Testing Information document must be a PDF file.");
		}
		if (formFields?.includeProfileDocument && !profileDocumentFilename) {
			errors.push("A Profile document must be provided if it is marked as included.");
		}
		if (formFields?.includeProfileDocument && profileDocumentFilename.length > 4 && profileDocumentFilename.slice(-4).toLowerCase() != '.pdf') {
			errors.push("The Profile document must be a PDF file.");
		}
		if (!formFields?.expiresOn) {
			errors.push("Expiration Date is required.");
		}

		if (errors.length > 0) {
			setErrorMessages(errors);
			scrollTo(0, 0);
			return false;
		}
		return true;
	}

	return (
		<section className="hwi-waste-characterization-add-form">
			{errorMessages?.length > 0 &&
				<Alert className="alert-error"
					severity="error"
					action={<IconButton onClick={() => { setErrorMessages(undefined); }} size='small'>X</IconButton>}>
					{(errorMessages?.length ?? 0) > 0 &&
						<ul className={errorMessages?.length == 1 ? 'single' : ''}>
							{errorMessages?.map((msg, index) => (
								<li key={index}>{msg}</li>
							))}
						</ul>
					}
				</Alert>
			}
			<LocalizationProvider dateAdapter={AdapterDayjs}>
				<Form onSubmit={() => handleSubmit()}>
					<div className="row">
						<InputLabel>Customer *</InputLabel>
						<Select
							className="customer-field"
							value={(formFields?.customerId ?? -1) > -1 ? formFields.customerId : ''}
							disabled={isLoadingCustomers}
							required
							fullWidth
							onChange={(event) => { setFormFields({ ...formFields, ["customerId"]: +event.target.value }) }}
						>
							{isLoadingCustomers ? (
								<MenuItem value="0" key="0">Loading...</MenuItem>
							) : (
								customerOptions.map((item, index) => (
									<MenuItem value={item.value} key={index} >{item.name}</MenuItem>
								))
							)}
						</Select>
					</div>
					<div className="row">
						<InputLabel>Material *</InputLabel>
						<Select
							className="material-field"
							value={(formFields?.materialId ?? -1) > -1 ? formFields.materialId : ''}
							disabled={isLoadingMaterialPrices}
							required
							fullWidth
							onChange={(event) => { setFormFields({ ...formFields, ["materialId"]: +event.target.value }) }}
						>
							{isLoadingMaterialPrices ? (
								<MenuItem value="0" key="0">Loading...</MenuItem>
							) : (
								materialOptions.map((item, index) => (
									<MenuItem value={item.value} key={index} >{item.name}</MenuItem>
								))
							)}
						</Select>
					</div>
					<div className="row">
						<InputLabel className="constituents-label">Constituents <span className={`counter${formFields?.constituents?.length == 500 ? ' exceeded' : ''}`}>{formFields?.constituents?.length ?? 0}/500</span></InputLabel>
						<TextField name="constituents" className="constituents-field" multiline variant="outlined" value={formFields?.constituents ?? ''} size="small" inputProps={{ maxLength: 500 }} onChange={(event) => { setFormFields({ ...formFields, ["constituents"]: event.target.value }) }}></TextField>
					</div>
					<div className="row">
						<InputLabel className="dot-waste-stream-label">DOT Waste Stream Name <span className={`counter${formFields?.dotWasteStream?.length == 150 ? ' exceeded' : ''}`}>{formFields?.dotWasteStream?.length ?? 0}/150</span></InputLabel>
						<TextField name="dotWasteStream" className="dot-waste-stream-field" multiline variant="outlined" value={formFields?.dotWasteStream ?? ''} size="small" inputProps={{ maxLength: 150 }} onChange={(event) => { setFormFields({ ...formFields, ["dotWasteStream"]: event.target.value }) }}></TextField>
					</div>
					<div className="row">
						<InputLabel>UN Number</InputLabel>
						<TextField name="unNumber" className="un-number-field" variant="outlined" value={formFields?.unNumber ?? ''} size="small" inputProps={{ maxLength: 10 }} onChange={(event) => { setFormFields({ ...formFields, ["unNumber"]: event.target.value }) }}></TextField>
					</div>
					<div className="row">
						<InputLabel>Process Generating Waste</InputLabel>
						<TextField name="generatingProcess" className="generating-process-field" variant="outlined" value={formFields?.generatingProcess ?? ''} size="small" inputProps={{ maxLength: 100 }} onChange={(event) => { setFormFields({ ...formFields, ["generatingProcess"]: event.target.value }) }}></TextField>
					</div>
					<div className="row">
						<InputLabel>Physical State</InputLabel>
						<Select
							className="physical-state-field"
							value={formFields?.physicalState ?? ''}
							fullWidth
							onChange={(event) => { setFormFields({ ...formFields, ["physicalState"]: event.target.value === "0" ? undefined : event.target.value }) }}
						>
							{formFields?.physicalState && <MenuItem value="0" key={0}>(Clear Selection)</MenuItem>}
							{physicalStateOptions.map((item, index) => (
								<MenuItem value={item} key={index}>{item}</MenuItem>
							))}
						</Select>
					</div>
					<div className="row">
						<InputLabel>Waste Stream Determination</InputLabel>
						<div className="waste-stream-determination-options">
							<FormControlLabel control={<Checkbox name="byProcess" className={formFields.byProcess ? 'is-checked' : ''} checked={formFields.byProcess} onChange={handleByProcessChange}></Checkbox>} label="Waste Determination based on (Process Evaluation, SDS's)" />
							<FormControlLabel control={<Checkbox name="byTesting" className={formFields.byTesting ? 'is-checked' : ''} checked={formFields.byTesting} onChange={handleByTestingChange}></Checkbox>} label="Waste Analysis (attach TCLP Testing Information)" />
						</div>
					</div>
					{/* SDS Upload section */}
					{formFields?.byProcess &&
						<div className="row">
							<InputLabel>SDS *</InputLabel>
							<div className="custom-uploader">
								<InputLabel htmlFor="processUploader" className="uploader-label">Choose</InputLabel>
								<input type="text" disabled value={processDocFilename}></input>
							</div>
							<input type="file" id="processUploader" name="processUploader" ref={processDocRef} className="uploader" accept="application/pdf" onChange={(event) => { handleProcessDocChange(event) }} />
						</div>
					}
					{/* TCLP Upload section  */}
					{formFields?.byTesting &&
						<div className="row">
							<InputLabel>TCLP Testing Information *</InputLabel>
							<div className="custom-uploader">
								<InputLabel htmlFor="testingUploader" className="uploader-label">Choose</InputLabel>
								<input type="text" disabled value={testingDocFilename}></input>
							</div>
							<input type="file" id="testingUploader" name="testingUploader" ref={testingDocRef} className="uploader" accept="application/pdf" onChange={(event) => { handleTestingDocChange(event) }} />
						</div>
					}
					<div className="row">
						<div className="include-profile-document-option">
							<FormControlLabel control={<Checkbox name="includeProfileDocument" className={formFields.includeProfileDocument ? 'is-checked' : ''} checked={formFields.includeProfileDocument} onChange={handleIncludeProfileDocumentChange}></Checkbox>} label="Include Profile document" />
						</div>
					</div>
					{/* Profile Document Upload section  */}
					{formFields?.includeProfileDocument &&
						<div className="row">
							<InputLabel>Profile document *</InputLabel>
							<div className="custom-uploader">
								<InputLabel htmlFor="profileDocumentUploader" className="uploader-label">Choose</InputLabel>
								<input type="text" disabled value={profileDocumentFilename}></input>
							</div>
							<input type="file" id="profileDocumentUploader" name="profileDocumentUploader" ref={profileDocumentRef} className="uploader" accept="application/pdf" onChange={(event) => { handleProfileDocumentChange(event) }} />
						</div>
					}
					<div className="row">
						<InputLabel>Is the waste a "solid waste" according to &sect;261.2?</InputLabel>
						<div className='is-solid-container'>
							<ButtonGroup variant="outlined">
								<Button className="is-solid" variant={formFields.isSolid ? "outlined" : "contained"} onClick={() => { setFormFields({ ...formFields, ["isSolid"]: false }) }}>No</Button>
								<Button className='is-solid' variant={formFields.isSolid ? "contained" : "outlined"} onClick={() => { setFormFields({ ...formFields, ["isSolid"]: true }) }}>Yes</Button>
							</ButtonGroup>
							<TextField
								name="solidExclusion"
								className={`solid-exclusion-field ${formFields?.isSolid ? 'hidden' : ''}`}
								required={!formFields?.isSolid}
								variant="outlined"
								value={formFields?.isSolidExclusion ?? ''}
								placeholder="Please specify exclusion..."
								size="small"
								inputProps={{ maxLength: 50 }}
								onChange={(event) => { setFormFields({ ...formFields, ["isSolidExclusion"]: event.target.value }) }}>
							</TextField>
						</div>
					</div>
					<div className="row">
						<InputLabel>Is the waste a characteristic hazardous waste?</InputLabel>
						<div className='characteristic-hazardous-waste-options'>
							<div className='is-ignitable-container'>
								<ButtonGroup variant="outlined">
									<Button className="no" variant={formFields.isIgnitable ? "outlined" : "contained"} onClick={() => { setFormFields({ ...formFields, ["isIgnitable"]: false }) }}>No</Button>
									<Button className='yes' variant={formFields.isIgnitable ? "contained" : "outlined"} onClick={() => { setFormFields({ ...formFields, ["isIgnitable"]: true }) }}>IGNITABLE (D001)</Button>
								</ButtonGroup>
								<TextField
									name="flashPoint"
									className={`flashpoint-field ${formFields?.isIgnitable ? '' : 'hidden'}`}
									required={formFields?.isIgnitable}
									variant="outlined"
									value={formFields?.flashPoint ?? ''}
									placeholder="Please specify flash point..."
									size="small"
									inputProps={{ maxLength: 10 }}
									onChange={(event) => { setFormFields({ ...formFields, ["flashPoint"]: event.target.value }) }}>
								</TextField>
							</div>
							<div className='is-corrosive-container'>
								<ButtonGroup variant="outlined">
									<Button className="no" variant={formFields.isCorrosive ? "outlined" : "contained"} onClick={() => { setFormFields({ ...formFields, ["isCorrosive"]: false }) }}>No</Button>
									<Button className='yes' variant={formFields.isCorrosive ? "contained" : "outlined"} onClick={() => { setFormFields({ ...formFields, ["isCorrosive"]: true }) }}>CORROSIVE (D002)</Button>
								</ButtonGroup>
								<TextField
									name="ph"
									className={`ph-field ${formFields?.isCorrosive ? '' : 'hidden'}`}
									required={formFields?.isCorrosive}
									variant="outlined"
									value={formFields?.ph ?? ''}
									placeholder="Please specify pH..."
									size="small"
									inputProps={{ maxLength: 10 }}
									onChange={(event) => { setFormFields({ ...formFields, ["ph"]: event.target.value }) }}>
								</TextField>
							</div>
							<div className='is-reactive-container'>
								<ButtonGroup variant="outlined">
									<Button className="no" variant={formFields.isReactive ? "outlined" : "contained"} onClick={() => { setFormFields({ ...formFields, ["isReactive"]: false }) }}>No</Button>
									<Button className='yes' variant={formFields.isReactive ? "contained" : "outlined"} onClick={() => { setFormFields({ ...formFields, ["isReactive"]: true }) }}>REACTIVE (D002)</Button>
								</ButtonGroup>
							</div>
							<div className='is-toxic-container'>
								<ButtonGroup variant="outlined">
									<Button className="no" variant={formFields.isToxic ? "outlined" : "contained"} onClick={() => { setFormFields({ ...formFields, ["isToxic"]: false }) }}>No</Button>
									<Button className='yes' variant={formFields.isToxic ? "contained" : "outlined"} onClick={() => { setFormFields({ ...formFields, ["isToxic"]: true }) }}>TOXIC (D004 - D043)</Button>
								</ButtonGroup>
								<TextField
									name="toxicityCodes"
									className={`toxic-codes-field ${formFields?.isToxic ? '' : 'hidden'}`}
									required={formFields?.isToxic}
									variant="outlined"
									value={formFields?.toxicityCodes ?? ''}
									placeholder="Please specify codes..."
									size="small"
									inputProps={{ maxLength: 50 }}
									onChange={(event) => { setFormFields({ ...formFields, ["toxicityCodes"]: event.target.value }) }}>
								</TextField>
							</div>
						</div>
					</div>
					<div className="row">
						<InputLabel>RCRA Waste Determination</InputLabel>
						<Select
							className="rcra-waste-determination-field"
							value={formFields?.rcraWasteDetermination ?? ''}
							fullWidth
							onChange={(event) => { setFormFields({ ...formFields, ["rcraWasteDetermination"]: event.target.value === "0" ? undefined : event.target.value }) }}
						>
							{formFields?.rcraWasteDetermination && <MenuItem value="0" key={0}>(Clear Selection)</MenuItem>}
							{rcraWasteDeterminationOptions.map((item, index) => (
								<MenuItem value={item} key={index}>{item}</MenuItem>
							))}
						</Select>
					</div>
					<div className="row">
						<InputLabel>Are land disposal restrictions (LDR) applicable?</InputLabel>
						<div className='is-ldr-applicable-container'>
							<ButtonGroup variant="outlined">
								<Button className="is-ldr-applicable" variant={formFields.isLdrApplicable ? "outlined" : "contained"} onClick={() => { setFormFields({ ...formFields, ["isLdrApplicable"]: false }) }}>No</Button>
								<Button className='is-ldr-applicable' variant={formFields.isLdrApplicable ? "contained" : "outlined"} onClick={() => { setFormFields({ ...formFields, ["isLdrApplicable"]: true }) }}>Yes</Button>
							</ButtonGroup>
						</div>
					</div>
					<div className="row">
						<InputLabel>Treatable in Wastewater?</InputLabel>
						<div className='is-wastewater-treatable-container'>
							<ButtonGroup variant="outlined">
								<Button className="is-wastewater-treatable" variant={formFields.isWastewaterTreatable ? "outlined" : "contained"} onClick={() => { setFormFields({ ...formFields, ["isWastewaterTreatable"]: false }) }}>No</Button>
								<Button className='is-wastewater-treatable' variant={formFields.isWastewaterTreatable ? "contained" : "outlined"} onClick={() => { setFormFields({ ...formFields, ["isWastewaterTreatable"]: true }) }}>Yes</Button>
							</ButtonGroup>
						</div>
					</div>
					<div className="row">
						<InputLabel>Expiration Date</InputLabel>
						<LocalizationProvider dateAdapter={AdapterDayjs}>
							<DatePicker className="expiration-date-field" timezone="system" value={dayjs(formFields.expiresOn)} onChange={(newValue) => { setFormFields({ ...formFields, ["expiresOn"]: newValue?.toDate() }); }} />
						</LocalizationProvider>
					</div>
					<div className="row">
						<InputLabel>Person completing form *</InputLabel>
						<div className='created-by-container'>
							<TextField name="createdBy" className='name' required variant="outlined" value={formFields?.createdBy ?? ''} placeholder='Name...' size="small" inputProps={{ maxLength: 50 }} onChange={(event) => { setFormFields({ ...formFields, ["createdBy"]: event.target.value }) }}></TextField>
							<TextField name="createdByTitle" className='title' required variant="outlined" value={formFields?.createdByTitle ?? ''} placeholder='Title...' size="small" inputProps={{ maxLength: 100 }} onChange={(event) => { setFormFields({ ...formFields, ["createdByTitle"]: event.target.value }) }}></TextField>
							<TextField name="createdByEmail" type="email" className='email' required variant="outlined" value={formFields?.createdByEmail ?? ''} placeholder='Email address...' size="small" inputProps={{ maxLength: 100 }} onChange={(event) => { setFormFields({ ...formFields, ["createdByEmail"]: event.target.value }) }}></TextField>
						</div>
					</div>
					<div className="row">
						<InputLabel>Environmental Manager</InputLabel>
						<div className='environmental-manager-container'>
							<TextField name="environmentalManagerName" className='name' variant="outlined" value={formFields?.environmentalManagerName ?? ''} placeholder='Name...' size="small" inputProps={{ maxLength: 50 }} onChange={(event) => { setFormFields({ ...formFields, ["environmentalManagerName"]: event.target.value }) }}></TextField>
							<TextField name="environmentalManagerTitle" className='title' variant="outlined" value={formFields?.environmentalManagerTitle ?? ''} placeholder='Title...' size="small" inputProps={{ maxLength: 100 }} onChange={(event) => { setFormFields({ ...formFields, ["environmentalManagerTitle"]: event.target.value }) }}></TextField>
							<TextField name="environmentalManagerEmail" type="email" className='email' variant="outlined" value={formFields?.environmentalManagerEmail ?? ''} placeholder='Email address...' size="small" inputProps={{ maxLength: 100 }} onChange={(event) => { setFormFields({ ...formFields, ["environmentalManagerEmail"]: event.target.value }) }}></TextField>
						</div>
					</div>
					<div className="row controls">
						<Button type="submit" className="save-button" variant="contained" disabled={isSaving}>{isSaving ? "Saving..." : "Save"}</Button>
					</div>
					{/* <Json data={formFields} title='Form Fields'></Json> */}
				</Form>
			</LocalizationProvider>
		</section>
	)
}

export default WasteCharacterizationAddForm;